import React, { useState, useEffect } from 'react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

// ─── Amazon search results ────────────────────────────────────────────────────
function AmazonResults({ searchName, currentAsin, onSelect }) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        authService.authenticatedFetch(`${API_BASE}/api/amazon/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ search_term: searchName, search_type: 'name' }),
        })
            .then(r => r.json())
            .then(data => setResults((data.products || []).filter(p => p.asin !== currentAsin)))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [searchName, currentAsin]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-500">
            <div className="flex gap-1.5">
                {[0, 1, 2].map(i => (
                    <div key={i} className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
            </div>
            <p className="text-sm">Searching Amazon for bundles & packs…</p>
        </div>
    );

    if (!results.length) return (
        <div className="text-center py-12 text-gray-400 text-sm">No other listings found.</div>
    );

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {results.map((p, i) => (
                <button key={i} onClick={() => onSelect(p)}
                    className="text-left border border-gray-200 rounded-xl p-3 hover:border-orange-400 hover:shadow-md transition-all duration-200 bg-white group">
                    <div className="w-full h-20 flex items-center justify-center mb-2 bg-gray-50 rounded-lg overflow-hidden">
                        <img src={p.image} alt="" className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-200"
                            onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">{p.title}</p>
                    <p className="text-[10px] text-gray-400 font-mono mb-1">{p.asin}</p>
                    <p className="text-sm font-bold text-orange-600">${p.price?.toFixed(2)}</p>
                    {p.monthly_sold > 0 && (
                        <p className="text-[10px] text-gray-400 mt-0.5">{p.monthly_sold}+ sold/mo</p>
                    )}
                    {p.sales_rank > 0 && (
                        <p className="text-[10px] text-gray-400">BSR #{p.sales_rank.toLocaleString()}</p>
                    )}
                </button>
            ))}
        </div>
    );
}

// ─── Walmart search results ───────────────────────────────────────────────────
function WalmartResults({ searchName, currentItemId, onSelect }) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        authService.authenticatedFetch(`${API_BASE}/api/walmart/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ search_term: searchName, search_type: 'name' }),
        })
            .then(r => r.json())
            .then(data => {
                const arr = Array.isArray(data) ? data : (data.products || []);
                setResults(arr.filter(p => String(p.item_id) !== String(currentItemId)));
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [searchName, currentItemId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-500">
            <div className="flex gap-1.5">
                {[0, 1, 2].map(i => (
                    <div key={i} className="w-2 h-2 rounded-full bg-blue-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
            </div>
            <p className="text-sm">Searching Walmart for bundles & packs…</p>
        </div>
    );

    if (!results.length) return (
        <div className="text-center py-12 text-gray-400 text-sm">No other listings found.</div>
    );

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {results.map((p, i) => (
                <button key={i} onClick={() => onSelect(p)}
                    className="text-left border border-gray-200 rounded-xl p-3 hover:border-blue-400 hover:shadow-md transition-all duration-200 bg-white group">
                    <div className="w-full h-20 flex items-center justify-center mb-2 bg-gray-50 rounded-lg overflow-hidden">
                        <img src={p.image} alt="" className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-200"
                            onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">{p.name}</p>
                    <p className="text-sm font-bold text-blue-600">${Number(p.price_value || p.price || 0).toFixed(2)}</p>
                    {p.rating > 0 && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                            ⭐ {p.rating.toFixed(1)} · {(p.number_of_reviews || p.review_count || 0).toLocaleString()} reviews
                        </p>
                    )}
                </button>
            ))}
        </div>
    );
}

// ─── Main button + modal ──────────────────────────────────────────────────────
const FindBundlesButton = ({ deal }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const marketplace = deal.marketplace;
    const data = deal.marketplace_data || {};

    // Build the search term — title without brand fluff, trimmed
    const rawTitle = data.title || data.name || deal.title || '';
    const searchName = rawTitle.replaceAll(',', '').trim().slice(0, 80);

    const currentId = marketplace === 'amazon' ? data.asin : data.item_id;
    const accentColor = marketplace === 'amazon' ? 'orange' : 'blue';
    const amazonDomain = authService.getAmazonMarketplace();

    const handleSelect = (product) => {
        const url = product.asin
            ? `https://www.amazon${amazonDomain}/dp/${product.asin}`
            : `https://www.walmart.com/ip/${product.item_id}`;
        window.open(url, '_blank');
    };

    const handleApply = () => {
        if (!selected) return;

        // Fire onSettingsChange equivalent — just reload prices by dispatching a storage event
        // The parent DealCard will re-render naturally since we're not mutating deal here.
        // Instead we alert the user with the key info to manually update if needed.
        // For a full integration, wire up deal mutation here.
        alert(
            `Selected:\n` +
            `Title: ${selected.title || selected.name}\n` +
            `ASIN/ID: ${selected.asin || selected.item_id}\n` +
            `Price: $${(selected.price || selected.price_value || 0)}`
        );
        setOpen(false);
        setSelected(null);
    };

    const colorClasses = {
        orange: {
            button: 'from-orange-400 to-amber-500 shadow-orange-200',
            badge: 'bg-orange-100 text-orange-700 border-orange-300',
            selectedBorder: 'border-orange-500 bg-orange-50',
            applyBtn: 'bg-orange-500 hover:bg-orange-600',
        },
        blue: {
            button: 'from-blue-400 to-indigo-500 shadow-blue-200',
            badge: 'bg-blue-100 text-blue-700 border-blue-300',
            selectedBorder: 'border-blue-500 bg-blue-50',
            applyBtn: 'bg-blue-500 hover:bg-blue-600',
        },
    }[accentColor];

    return (
        <>
            {/* ── Glowing button ── */}
            <button
                onClick={() => setOpen(true)}
                className={`gap-1 flex items-center justify-center px-2 rounded font-bold text-xs opacity-[0.9] text-white bg-gradient-to-r ${colorClasses.button} hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 animate-pulse-slow`}
            >
                <span className="text-base">📦</span>
                Find Bundles
            </button>

            {/* ── Modal ── */}
            {open && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
                    onClick={() => { setOpen(false); setSelected(null); }}>
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[70vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className={`bg-gradient-to-r ${colorClasses.button} px-6 py-5 text-white flex items-center justify-between flex-shrink-0`}>
                            <div>
                                <h2 className="text-xl font-extrabold leading-tight">Find Bundles & Multi-Packs</h2>
                                <p className="text-white/80 text-sm mt-0.5 line-clamp-1">"{searchName}"</p>
                            </div>
                            <button onClick={() => { setOpen(false); setSelected(null); }}
                                className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition text-white font-bold text-lg">
                                ✕
                            </button>
                        </div>

                        {/* Results */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {marketplace === 'amazon' ? (
                                <AmazonResults
                                    searchName={searchName}
                                    currentAsin={currentId}
                                    onSelect={handleSelect}
                                />
                            ) : (
                                <WalmartResults
                                    searchName={searchName}
                                    currentItemId={currentId}
                                    onSelect={handleSelect}
                                />
                            )}
                        </div>

                        {/* Footer note */}
                        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                            <p className="text-xs text-gray-400 text-center">
                                Click any product to open it. Results are live from {marketplace === 'amazon' ? 'Amazon' : 'Walmart'}.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FindBundlesButton;